import { z } from 'zod'

const optionValuesSchema = z.object({
  id: z.string(),
  name: z.string(),
  hasVariants: z.boolean(),
})

const inventoryItemSchema = z.object({
  id: z.string(),
  tracked: z.boolean(),
  requiresShipping: z.boolean(),
})

const variantSchema = z.object({
  id: z.string(),
  title: z.string(),
  price: z.coerce.number(),
  sku: z.string().nullable(),
  barcode: z.string().nullable(),
  inventoryQuantity: z.coerce.number(),
  availableForSale: z.boolean(),
  selectedOptions: z.array(
    z.object({
      name: z.string(),
      value: z.string(),
    })
  ),

  inventoryItem: inventoryItemSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
})

export const shopifyProductSchema = z.object({
  id: z.string(),
  title: z.string(),
  handle: z.string(),
  status: z.string(),
  productType: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  category: z.object({
    id: z.string(),
    name: z.string(),
    fullName: z.string(),
  }),
  options: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      values: z.array(z.string()),
      optionValues: z.array(optionValuesSchema),
    })
  ),
  variants: z.object({
    nodes: z.array(variantSchema),
  }),
})

export type ShopifyProduct = z.infer<typeof shopifyProductSchema>

export const shopifyOrderSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  displayFinancialStatus: z.string().nullable(),
  displayFulfillmentStatus: z.string(),
  totalPriceSet: z.object({
    shopMoney: z.object({ amount: z.string(), currencyCode: z.string() }),
  }),
  lineItems: z.object({
    nodes: z.array(
      z.object({
        id: z.string(),
        title: z.string(),
        quantity: z.number(),
        variant: z
          .object({
            id: z.string(),
            sku: z.string().nullable(),
            price: z.string(),
          })
          .nullable(),
      })
    ),
  }),
  shippingAddress: z
    .object({
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      address1: z.string().nullable(),
      city: z.string().nullable(),
      country: z.string().nullable(),
      zip: z.string().nullable(),
    })
    .nullable(),
})

export type ShopifyOrder = z.infer<typeof shopifyOrderSchema>
